<script setup>
import { onMounted, reactive, watch } from "vue";
import { Link, usePage, router } from "@inertiajs/vue3";
import HeaderMenu from "@/Components/header/HeaderMenu.vue";
import Button from "@/Components/ui/button/Button.vue";
import SearchBar from "@/Components/header/SearchBar.vue";
import SearchIcon from "@/Components/svg/SearchIcon.vue";
import { cn } from "@/lib/utils.js";
import PageLink from "@/Components/PageLink.vue";
import CloseIcon from "@/Components/svg/CloseIcon.vue";
import Loading from "@/Components/svg/Loading.vue";

const {
  props: {
    user,
    siteSettings: { logo, main_menu, footer_menu, login_page_route },
  },
} = usePage();

const state = reactive({
  searchEnabled: false,
  mobileMenuOpen: false,
  loading: false,
});

watch(
  () => state.mobileMenuOpen,
  (value) => {
    if (value) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
  },
);

onMounted(() => {
  document.body.style.overflow = "auto";
});

const handleLogInOut = () => {
  if (user) {
    state.loading = true;
    router.post(route("members.logout"));
    setTimeout(() => {
      state.loading = false;
      window.location.reload();
    }, 3000);
    return;
  }
  router.visit(login_page_route);
};
</script>

<template>
  <div
    id="page-header"
    class="print:hidden w-full flex flex-wrap min-[1205px]:flex-row min-[1205px]:px-6 h-14 min-[1205px]:h-28 items-start border-b border-b-brand-dark-blue fixed top-0 left-0 bg-brand-light z-40"
  >
    <div
      class="w-full flex h-full flex-wrap min-[1205px]:flex-row min-[1205px]:items-center"
    >
      <div
        class="w-5/12 flex items-center min-[1205px]:w-3/12 pl-3 min-[1205px]:pl-0"
      >
        <Link href="/">
          <img
            :src="logo"
            alt="Logo"
            class="w-full h-auto max-w-[155px] min-[1205px]:max-w-[200px]"
          />
        </Link>
      </div>
      <div
        :class="
          cn('hidden min-[1205px]:block min-[1205px]:w-6/12', {
            'invisible pointer-events-none': state.searchEnabled,
          })
        "
      >
        <HeaderMenu ref="desktop-menu" :items="main_menu" />
      </div>
      <div
        class="w-7/12 min-[1205px]:w-3/12 flex justify-end min-[1205px]:order-last pr-3 min-[1205px]:pr-0"
      >
        <div class="space-x-2 items-center flex">
          <Button
            class="px-2 uppercase"
            :class="cn({ hidden: state.searchEnabled })"
            variant="link"
            @click="state.searchEnabled = !state.searchEnabled"
          >
            <SearchIcon />
          </Button>
          <Button
            class="pl-2 hidden link min-[1205px]:flex pr-0 uppercase cursor-pointer"
            :class="cn({ '!hidden': state.searchEnabled })"
            variant="link"
            @click="handleLogInOut"
          >
            <span class="h6 !m-0 !text-brand-blue"
              >Member {{ user ? "Logout" : "Login" }}</span
            >
            <Loading v-if="state.loading" />
          </Button>
          <Button
            @click="state.mobileMenuOpen = !state.mobileMenuOpen"
            class="pl-0 flex link min-[1205px]:hidden pr-0 uppercase"
            variant="link"
          >
            <span class="!text-brand-blue text-[14px] capitalize">Menu</span>
          </Button>
          <Button
            v-if="state.searchEnabled"
            @click="state.searchEnabled = !state.searchEnabled"
            variant="link"
            class="pr-0"
          >
            <CloseIcon />
          </Button>
        </div>
      </div>
    </div>
    <SearchBar
      :enabled="state.searchEnabled"
      @escape-key-down="state.searchEnabled = false"
    />
  </div>
  <div
    :class="state.mobileMenuOpen ? 'h-dvh' : 'h-0'"
    class="w-full fixed pt-12 top-0 left-0 bg-brand-light min-[1205px]:hidden transition-all overflow-hidden duration-300 z-30"
  >
    <HeaderMenu
      class="!mb-1 !px-3"
      ref="mobile-menu"
      ref-name="mobile-menu"
      :items="main_menu"
      :mobile-menu-open="state.mobileMenuOpen"
      @closeMobileMenu="(value) => (state.mobileMenuOpen = false)"
    />
    <div class="border-t border-t-brand-dark-blue">
      <ul class="group !px-3 min-[1205px]:!pl-0 !list-none text-right">
        <li v-for="(item, index) in footer_menu" :key="index" class="!m-0">
          <PageLink
            :link="item.link"
            :show-arrow="false"
            class="!p-0 h-auto font-bold !leading-none uppercase font-sans text-[10px]"
            @pageLinkClicked="state.mobileMenuOpen = false"
          >
            {{ item.link.title }}
          </PageLink>
        </li>
      </ul>
    </div>
  </div>
</template>

<style scoped></style>
